"use client";

import React, { useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { EstimatorSelections, StepId, Feature, PriceBreakdown } from './types';
import { STEPS, calculatePrice } from './constants';
import EstimatorStep from './EstimatorStep';
import ResultsScreen from './ResultsScreen';

const INITIAL_SELECTIONS: EstimatorSelections = {
  orgType: '',
  projectType: '',
  pageCount: '',
  features: [],
  contentReadiness: '',
  monthlySupport: '',
};

const CostEstimator: React.FC = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selections, setSelections] = useState<EstimatorSelections>(INITIAL_SELECTIONS);
  const [direction, setDirection] = useState<'forward' | 'backward' | 'none'>('forward');
  const [isCalculated, setIsCalculated] = useState(false);

  const currentStep = STEPS[currentStepIndex];

  const handleSelect = useCallback((field: keyof EstimatorSelections, value: string) => {
    setSelections((prev) => {
      if (Array.isArray(prev[field])) {
        // Multi-select logic (features)
        const currentArr = prev[field] as Feature[];
        const isSelected = currentArr.includes(value as Feature);
        
        return {
          ...prev,
          [field]: isSelected
            ? currentArr.filter((item) => item !== value)
            : [...currentArr, value as Feature],
        };
      }

      // Single-select logic
      return {
        ...prev,
        [field]: value,
      };
    });
  }, []);

  const handleNext = useCallback(() => {
    if (currentStepIndex < STEPS.length - 1) {
      setDirection('forward');
      setCurrentStepIndex((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsCalculated(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStepIndex]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setDirection('backward');
      setCurrentStepIndex((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStepIndex]);

  const handleStartOver = useCallback(() => {
    setSelections(INITIAL_SELECTIONS);
    setCurrentStepIndex(0);
    setIsCalculated(false);
    setDirection('none');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const breakdown: PriceBreakdown = calculatePrice(selections);

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="overflow-hidden">
        <div 
          className={cn(
            "min-h-[500px] flex flex-col",
            isCalculated ? "bg-white rounded-[2.5rem] border border-bloom-border p-8 md:p-12 shadow-sm" : "bg-transparent"
          )}
        >
          {!isCalculated ? (
            <EstimatorStep
              key={currentStep.id}
              step={currentStep}
              stepIndex={currentStepIndex}
              totalSteps={STEPS.length}
              selections={selections}
              onSelect={handleSelect}
              onNext={handleNext}
              onBack={handleBack}
              isFirst={currentStepIndex === 0}
              animationClass={
                direction === 'forward' 
                  ? 'animate-slide-in-right' 
                  : direction === 'backward' 
                  ? 'animate-slide-in-left' 
                  : ''
              }
            />
          ) : (
            <ResultsScreen
              selections={selections}
              breakdown={breakdown}
              onStartOver={handleStartOver}
            />
          )}
        </div>
      </div>
      
      {/* Visual background decoration */}
      {!isCalculated && (
        <div className="mt-12 text-center opacity-30 select-none pointer-events-none" aria-hidden="true">
          <p className="font-serif italic text-sm text-[#7A7367]">
            Investing in your mission, one pixel at a time.
          </p>
        </div>
      )}
    </div>
  );
};

export default CostEstimator;
